import React, { useState, useEffect } from 'react';
import { 
  Package, Clock, CheckCircle, Truck, MapPin, ChevronRight, 
  ArrowLeft, ShoppingBag, AlertCircle, RefreshCw, CreditCard,
  Calendar, Receipt, Phone, Mail, Box, Navigation
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ordersAPI } from '../services/api';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

const MyOrders = ({ user, onBack, onTrackOrder }) => {
  const { t } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Extended status config for new order statuses
  const statusConfig = {
    'pending': { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: Clock, label: t('pending'), description: t('pending') },
    'payment_confirmed': { color: 'bg-green-100 text-green-700 border-green-200', icon: CreditCard, label: 'Payment Confirmed', description: 'Payment received' },
    'picking': { color: 'bg-orange-100 text-orange-700 border-orange-200', icon: Package, label: 'Being Prepared', description: 'Items being picked' },
    'picked': { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: CheckCircle, label: 'Packed', description: 'Ready for delivery' },
    'confirmed': { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: CheckCircle, label: t('confirmed'), description: t('confirmed') },
    'preparing': { color: 'bg-purple-100 text-purple-700 border-purple-200', icon: Box, label: t('preparing'), description: t('preparing') },
    'assigned': { color: 'bg-purple-100 text-purple-700 border-purple-200', icon: Truck, label: 'Driver Assigned', description: 'Driver on the way' },
    'driver_to_store': { color: 'bg-indigo-100 text-indigo-700 border-indigo-200', icon: Truck, label: 'Driver to Store', description: 'Driver heading to store' },
    'driver_at_store': { color: 'bg-indigo-100 text-indigo-700 border-indigo-200', icon: MapPin, label: 'At Store', description: 'Driver at store' },
    'out_for_delivery': { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Truck, label: 'Out for Delivery', description: 'On the way!' },
    'in_transit': { color: 'bg-indigo-100 text-indigo-700 border-indigo-200', icon: Truck, label: t('outForDelivery'), description: t('outForDelivery') },
    'delivered': { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle, label: t('delivered'), description: t('delivered') },
    'ready_for_pickup': { color: 'bg-green-100 text-green-700 border-green-200', icon: MapPin, label: 'Ready for Pickup', description: 'Pick up at store' },
    'picked_up': { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle, label: 'Picked Up', description: 'Order picked up' },
    'cancelled': { color: 'bg-red-100 text-red-700 border-red-200', icon: AlertCircle, label: t('cancelled'), description: t('cancelled') },
  };

  const paymentStatusConfig = {
    'pending': { color: 'bg-yellow-50 text-yellow-600', label: t('pending') },
    'confirmed': { color: 'bg-green-50 text-green-600', label: t('confirmed') },
    'rejected': { color: 'bg-red-50 text-red-600', label: t('failed') },
  };

  // Check if order is trackable (from order placed to delivery)
  const isTrackable = (status) => {
    return ['pending', 'payment_confirmed', 'picking', 'picked', 'confirmed', 'preparing', 'assigned', 'driver_to_store', 'driver_at_store', 'out_for_delivery'].includes(status);
  };

  // Fetch orders
  const fetchOrders = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const data = await ordersAPI.getAll();
      setOrders(data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error(t('somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [user]);

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // If user not logged in
  if (!user) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="orders-login-prompt">
        <div className="text-center">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700">Please sign in to view your orders</h2>
        </div>
      </div>
    );
  }

  // Order Detail View
  if (selectedOrder) {
    const status = statusConfig[selectedOrder.status] || statusConfig['pending'];
    const StatusIcon = status.icon;
    const paymentStatus = paymentStatusConfig[selectedOrder.payment_status] || paymentStatusConfig['pending'];

    return (
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="order-detail-page">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => setSelectedOrder(null)}
          className="mb-4"
          data-testid="back-to-orders-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Orders
        </Button>

        {/* Order Header */}
        <Card className="p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Order Details</h1>
              <p className="text-gray-500 font-mono">#{selectedOrder.order_number || selectedOrder.id.slice(0, 8).toUpperCase()}</p>
            </div>
            <div className="flex items-center gap-3">
              <Badge className={`${status.color} border px-3 py-1 flex items-center gap-1`}>
                <StatusIcon className="w-4 h-4" />
                {status.label}
              </Badge>
              <Badge className={`${paymentStatus.color} px-3 py-1`}>
                {paymentStatus.label}
              </Badge>
            </div>
          </div>

          {/* Status Timeline */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-800 mb-3">Order Status</h3>
            <div className="flex items-center gap-2 text-sm">
              <div className={`flex items-center gap-1 ${selectedOrder.status !== 'cancelled' ? 'text-green-600' : 'text-gray-400'}`}>
                <CheckCircle className="w-4 h-4" />
                <span>Placed</span>
              </div>
              <div className={`flex-1 h-0.5 ${['confirmed', 'preparing', 'in_transit', 'delivered'].includes(selectedOrder.status) ? 'bg-green-500' : 'bg-gray-200'}`} />
              <div className={`flex items-center gap-1 ${['confirmed', 'preparing', 'in_transit', 'delivered'].includes(selectedOrder.status) ? 'text-green-600' : 'text-gray-400'}`}>
                <CheckCircle className="w-4 h-4" />
                <span>Confirmed</span>
              </div>
              <div className={`flex-1 h-0.5 ${['preparing', 'in_transit', 'delivered'].includes(selectedOrder.status) ? 'bg-green-500' : 'bg-gray-200'}`} />
              <div className={`flex items-center gap-1 ${['preparing', 'in_transit', 'delivered'].includes(selectedOrder.status) ? 'text-green-600' : 'text-gray-400'}`}>
                <Box className="w-4 h-4" />
                <span>Preparing</span>
              </div>
              <div className={`flex-1 h-0.5 ${['in_transit', 'delivered'].includes(selectedOrder.status) ? 'bg-green-500' : 'bg-gray-200'}`} />
              <div className={`flex items-center gap-1 ${['in_transit', 'delivered'].includes(selectedOrder.status) ? 'text-green-600' : 'text-gray-400'}`}>
                <Truck className="w-4 h-4" />
                <span>In Transit</span>
              </div>
              <div className={`flex-1 h-0.5 ${selectedOrder.status === 'delivered' ? 'bg-green-500' : 'bg-gray-200'}`} />
              <div className={`flex items-center gap-1 ${selectedOrder.status === 'delivered' ? 'text-green-600' : 'text-gray-400'}`}>
                <CheckCircle className="w-4 h-4" />
                <span>Delivered</span>
              </div>
            </div>
          </div>

          {/* Order Info */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Order Date
              </h3>
              <p className="text-gray-600">{formatDate(selectedOrder.created_at)}</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Payment Method
              </h3>
              <p className="text-gray-600 capitalize">{selectedOrder.payment_method === 'cod' ? 'Cash on Delivery' : selectedOrder.payment_method}</p>
            </div>
          </div>
        </Card>

        {/* Order Items */}
        <Card className="p-6 mb-6">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" />
            Items ({selectedOrder.items.length})
          </h3>
          <div className="space-y-3">
            {selectedOrder.items.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between py-3 border-b last:border-0">
                <div>
                  <p className="font-medium text-gray-800">{item.quantity}x {item.name}</p>
                  <p className="text-sm text-gray-500">${item.price.toFixed(2)} each</p>
                </div>
                <p className="font-semibold text-gray-800">${(item.quantity * item.price).toFixed(2)}</p>
              </div>
            ))}
          </div>
          
          {/* Totals */}
          <div className="mt-4 pt-4 border-t space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Subtotal</span>
              <span>${selectedOrder.subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Tax (HST 13%)</span>
              <span>${selectedOrder.tax.toFixed(2)}</span>
            </div>
            {selectedOrder.delivery_fee > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Delivery Fee</span>
                <span>${selectedOrder.delivery_fee.toFixed(2)}</span>
              </div>
            )}
            {selectedOrder.gift_card_discount > 0 && (
              <div className="flex justify-between text-sm text-green-600">
                <span>Gift Card Discount</span>
                <span>-${selectedOrder.gift_card_discount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-bold pt-2 border-t">
              <span>Total</span>
              <span className="text-orange-600">${selectedOrder.total.toFixed(2)}</span>
            </div>
          </div>
        </Card>

        {/* Delivery Info */}
        <Card className="p-6">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Delivery Information
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500 mb-1">Recipient</p>
              <p className="font-medium text-gray-800">{selectedOrder.delivery_info.name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Address</p>
              <p className="font-medium text-gray-800">
                {selectedOrder.delivery_info.address}<br />
                {selectedOrder.delivery_info.city}, {selectedOrder.delivery_info.province} {selectedOrder.delivery_info.postal_code}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">{selectedOrder.delivery_info.phone || 'Not provided'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">{selectedOrder.delivery_info.email}</span>
            </div>
            {selectedOrder.delivery_info.instructions && (
              <div className="md:col-span-2">
                <p className="text-sm text-gray-500 mb-1">Delivery Instructions</p>
                <p className="text-gray-600">{selectedOrder.delivery_info.instructions}</p>
              </div>
            )}
          </div>
        </Card>
      </div>
    );
  }

  // Orders List View
  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="my-orders-page">
      {onBack && (
        <Button 
          variant="ghost" 
          onClick={onBack}
          className="mb-4 flex items-center gap-1 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Shop</span>
        </Button>
      )}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-1">My Orders</h1>
          <p className="text-gray-500 text-sm md:text-base">Track and manage your orders</p>
        </div>
        <Button 
          variant="outline" 
          onClick={fetchOrders}
          disabled={loading}
          data-testid="refresh-orders-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <Card key={i} className="p-4 animate-pulse">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-gray-200" />
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-1/4 mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-1/3" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : orders.length === 0 ? (
        <Card className="p-8 text-center" data-testid="no-orders-message">
          <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">No orders yet</h2>
          <p className="text-gray-500">Your order history will appear here once you make a purchase.</p>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="orders-list">
          {orders.map((order) => {
            const status = statusConfig[order.status] || statusConfig['pending'];
            const StatusIcon = status.icon;
            
            return (
              <Card 
                key={order.id} 
                className="overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setSelectedOrder(order)}
                data-testid={`order-card-${order.id}`}
              >
                <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                      <Package className="w-5 h-5 text-gray-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">#{order.order_number || order.id.slice(0, 8).toUpperCase()}</p>
                      <p className="text-sm text-gray-500">{formatDate(order.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={`${status.color} border flex items-center gap-1`}>
                      <StatusIcon className="w-3 h-3" />
                      {status.label}
                    </Badge>
                  </div>
                </div>

                <div className="p-4">
                  <div className="space-y-1 mb-4">
                    {order.items.slice(0, 3).map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-gray-600">{item.quantity}x {item.name}</span>
                        <span className="text-gray-800">${(item.quantity * item.price).toFixed(2)}</span>
                      </div>
                    ))}
                    {order.items.length > 3 && (
                      <p className="text-sm text-gray-400">+{order.items.length - 3} more items</p>
                    )}
                  </div>

                  {/* Track Order Button - shown for active deliveries */}
                  {isTrackable(order.status) && onTrackOrder && (
                    <Button
                      className="w-full mt-3 bg-blue-500 hover:bg-blue-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        onTrackOrder(order.id);
                      }}
                      data-testid={`track-order-${order.id}`}
                    >
                      <Navigation className="w-4 h-4 mr-2" />
                      Track Order Live
                    </Button>
                  )}

                  {/* Pickup Code - shown for store pickup orders */}
                  {order.order_type === 'pickup' && order.pickup_code && order.status === 'ready_for_pickup' && (
                    <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200 text-center">
                      <p className="text-sm text-green-600 mb-1">Your Pickup Code:</p>
                      <p className="text-2xl font-bold text-green-700 tracking-wider">{order.pickup_code}</p>
                      <p className="text-xs text-green-500 mt-1">Show this at the store</p>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <MapPin className="w-4 h-4" />
                      <span className="truncate max-w-[200px]">{order.delivery_info.address}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-bold text-gray-800">${order.total.toFixed(2)}</span>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyOrders;
